import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type AgentInboxContact, backendApis } from '@/backendApis';

const ADDRESS_KEY = ['agentInbox', 'address'];
const CONTACTS_KEY = ['agentInbox', 'contacts'];

export const useAgentInboxAddressQuery = () =>
  useQuery({
    queryKey: ADDRESS_KEY,
    queryFn: () => backendApis.agentInbox.getAddress(),
  });

export const useAgentInboxAvailabilityQuery = (address: string) =>
  useQuery({
    queryKey: ['agentInbox', 'availability', address],
    queryFn: () => backendApis.agentInbox.isAvailable(address),
    enabled: address.length > 0,
  });

export const useRenameAgentInboxAddressMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (address: string) => backendApis.agentInbox.rename(address),
    onSuccess: (address) => queryClient.setQueryData(ADDRESS_KEY, address),
  });
};

export const useAgentInboxMessagesQuery = (sent: boolean) =>
  useQuery({
    queryKey: ['agentInbox', 'messages', { sent }],
    queryFn: () => backendApis.agentInbox.listMessages(sent),
  });

export const useAgentInboxMessageQuery = (id: string) =>
  useQuery({
    queryKey: ['agentInbox', 'message', id],
    queryFn: () => backendApis.agentInbox.getMessage(id),
  });

export const useAgentInboxContactsQuery = () =>
  useQuery({
    queryKey: CONTACTS_KEY,
    queryFn: () => backendApis.agentInbox.listContacts(),
  });

export const useSaveAgentInboxContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contact: AgentInboxContact) =>
      backendApis.agentInbox.saveContact(contact),
    scope: { id: 'agentInboxContacts' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
};

export const useRemoveAgentInboxContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (address: string) =>
      backendApis.agentInbox.removeContact(address),
    scope: { id: 'agentInboxContacts' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
};
